import { useCallback, useEffect, useRef, useState } from "react";

export const LONG_PRESS_MS = 500;
export const LONG_PRESS_PULSE_MS = 220;

/** Brief opacity flash during long-press ack — dim selected, brighten dimmed. */
export function longPressPulseOpacity(baseOpacity: number, pulsing: boolean): number {
    if (!pulsing) return baseOpacity;
    if (baseOpacity >= 0.9) return 0.72;
    return Math.min(baseOpacity + 0.22, 0.78);
}
const MOVE_TOLERANCE_PX = 16;

/** Visible hint text (shown on all devices, e.g. under chart titles). */
export const MULTI_SELECT_HINT = "Tap to filter; hold to multi-select";

/** Hover-only title tooltips — desktop only; never shown on touch. */
export const MULTI_SELECT_HINT_TITLE = "Click to filter; ⌘-click or hold to multi-select";

export function isMultiSelectClick(
    event?: { metaKey?: boolean; ctrlKey?: boolean } | null,
    longPress?: boolean,
): boolean {
    return !!(event?.metaKey || event?.ctrlKey || longPress);
}

export function preventContextMenu(e: React.SyntheticEvent) {
    e.preventDefault();
    e.stopPropagation();
}

/** Block native context menu (right-click / long-press) on filter targets. */
export const CONTEXT_MENU_BLOCKERS = {
    onContextMenu: preventContextMenu,
    onContextMenuCapture: preventContextMenu,
} as const;

export type LongPressHandlers = {
    onPointerDown: (e: React.PointerEvent) => void;
    onPointerMove: (e: React.PointerEvent) => void;
    onPointerUp: (e: React.PointerEvent) => void;
    onPointerCancel: (e: React.PointerEvent) => void;
    onContextMenu: (e: React.MouseEvent) => void;
    onContextMenuCapture: (e: React.MouseEvent) => void;
};

/** Return a function to run the filter side-effect after the gesture ends (pointer-up/click). */
export type LongPressResult = boolean | void | (() => void);

interface LongPressOptions {
    onLongPress?: () => LongPressResult;
    /** Fires when the hold duration is reached — use for visual/haptic ack before release. */
    onLongPressRecognized?: () => void;
}

/** Brief highlight on a filter target when long-press threshold is reached. */
export function useLongPressPulse(durationMs = LONG_PRESS_PULSE_MS) {
    const [pulseKey, setPulseKey] = useState<string | null>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    useEffect(() => () => {
        if (timerRef.current) clearTimeout(timerRef.current);
    }, []);

    const triggerPulse = useCallback(
        (key: string) => {
            if (timerRef.current) clearTimeout(timerRef.current);
            setPulseKey(key);
            timerRef.current = setTimeout(() => setPulseKey(null), durationMs);
        },
        [durationMs],
    );

    return { pulseKey, triggerPulse };
}

function scheduleAfterClick(fn: () => void) {
    setTimeout(fn, 0);
}

/** Long-press (touch or mouse) as an alternative to ⌘-click multi-select. */
export function useLongPressMultiSelect(options?: LongPressOptions): {
    pressHandlers: LongPressHandlers;
    consumeLongPress: () => boolean;
    /** True when onLongPress already handled the gesture — skip the follow-up click. */
    consumeSuppressClick: () => boolean;
} {
    const onLongPressRef = useRef(options?.onLongPress);
    onLongPressRef.current = options?.onLongPress;
    const onRecognizedRef = useRef(options?.onLongPressRecognized);
    onRecognizedRef.current = options?.onLongPressRecognized;

    const longPressRef = useRef(false);
    const suppressClickRef = useRef(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const startRef = useRef<{ x: number; y: number } | null>(null);
    const pendingActionRef = useRef<(() => void) | null>(null);

    const clearTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = undefined;
        }
    }, []);

    const flushPendingAction = useCallback(() => {
        const action = pendingActionRef.current;
        if (!action) return;
        pendingActionRef.current = null;
        scheduleAfterClick(action);
    }, []);

    useEffect(() => () => clearTimer(), [clearTimer]);

    const pressHandlers: LongPressHandlers = {
        onPointerDown: (e) => {
            if (e.button !== 0) {
                if (e.button === 2) e.preventDefault();
                return;
            }
            clearTimer();
            pendingActionRef.current = null;
            longPressRef.current = false;
            suppressClickRef.current = false;
            startRef.current = { x: e.clientX, y: e.clientY };
            e.currentTarget.setPointerCapture(e.pointerId);
            timerRef.current = setTimeout(() => {
                longPressRef.current = true;
                if (e.pointerType === "touch") {
                    navigator.vibrate?.([10, 30, 10]);
                }
                onRecognizedRef.current?.();
                if (onLongPressRef.current) {
                    const result = onLongPressRef.current();
                    if (typeof result === "function") {
                        pendingActionRef.current = result;
                        suppressClickRef.current = true;
                    }
                    longPressRef.current = false;
                }
            }, LONG_PRESS_MS);
        },
        onPointerMove: (e) => {
            const start = startRef.current;
            if (!start) return;
            const dx = e.clientX - start.x;
            const dy = e.clientY - start.y;
            if (dx * dx + dy * dy > MOVE_TOLERANCE_PX * MOVE_TOLERANCE_PX) {
                clearTimer();
                longPressRef.current = false;
                startRef.current = null;
            }
        },
        onPointerUp: (e) => {
            clearTimer();
            startRef.current = null;
            if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                e.currentTarget.releasePointerCapture(e.pointerId);
            }
            flushPendingAction();
        },
        onPointerCancel: (e) => {
            clearTimer();
            startRef.current = null;
            longPressRef.current = false;
            if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                e.currentTarget.releasePointerCapture(e.pointerId);
            }
            if (pendingActionRef.current) {
                flushPendingAction();
            } else {
                suppressClickRef.current = false;
            }
        },
        onContextMenu: preventContextMenu,
        onContextMenuCapture: preventContextMenu,
    };

    const consumeLongPress = useCallback(() => {
        const was = longPressRef.current;
        longPressRef.current = false;
        return was;
    }, []);

    const consumeSuppressClick = useCallback(() => {
        const was = suppressClickRef.current;
        suppressClickRef.current = false;
        return was;
    }, []);

    return { pressHandlers, consumeLongPress, consumeSuppressClick };
}

/** Run extra logic (e.g. record press target) before base long-press handlers. */
export function mergeLongPressHandlers(
    base: LongPressHandlers,
    onPointerDownExtra?: (e: React.PointerEvent) => void,
    onPointerCancelExtra?: () => void,
): LongPressHandlers {
    return {
        onPointerDown: (e) => {
            onPointerDownExtra?.(e);
            base.onPointerDown(e);
        },
        onPointerMove: base.onPointerMove,
        onPointerUp: base.onPointerUp,
        onPointerCancel: (e) => {
            onPointerCancelExtra?.();
            base.onPointerCancel(e);
        },
        onContextMenu: base.onContextMenu,
        onContextMenuCapture: base.onContextMenuCapture,
    };
}
